import { useEffect, useRef } from "react";
import { useMap } from "react-leaflet";
import L from "leaflet";

// Renders territory prospect pins (color = sales status, letter = category) and,
// optionally, current advertisers (emerald "$" star). Clicking a prospect opens
// the prospect card (onSelect). Shared by the shop and admin territory maps.
export default function ProspectLayer({ prospects, advertisers = [], showAdvertisers = true, statusColor, catLetter, onSelect, readOnly = false }) {
  const map = useMap();
  const ref = useRef([]);

  useEffect(() => {
    if (!map) return undefined;
    ref.current.forEach((m) => map.removeLayer(m));
    ref.current = [];
    const esc = (s) => String(s == null ? "" : s).replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]));

    (prospects || []).forEach((p) => {
      if (p.latitude == null || p.longitude == null) return;
      const color = statusColor[p.status] || "#9ca3af";
      const letter = catLetter[p.category] || "?";
      const icon = L.divIcon({
        className: "prospect-pin",
        html: `<div style="width:22px;height:22px;border-radius:50% 50% 50% 0;transform:rotate(-45deg);background:${color};border:2px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,.45);display:flex;align-items:center;justify-content:center"><span style="transform:rotate(45deg);color:#fff;font-size:11px;font-weight:700">${letter}</span></div>`,
        iconSize: [22, 22], iconAnchor: [11, 22],
      });
      const mk = L.marker([p.latitude, p.longitude], { icon });
      if (readOnly) {
        const rows = [`<div style="font-weight:700;font-size:13px">${esc(p.name || "Business")}</div>`];
        if (p.category) rows.push(`<div style="font-size:11px;color:#6b7280;text-transform:capitalize">${esc(p.category)}</div>`);
        if (p.city) rows.push(`<div style="font-size:12px;margin-top:4px;color:#374151">${esc(p.city)}</div>`);
        if (p.status) rows.push(`<div style="font-size:11px;margin-top:4px;color:#374151;font-weight:600">Status: ${esc(p.status)}</div>`);
        mk.bindPopup(`<div style="min-width:160px">${rows.join("")}</div>`);
      } else {
        mk.on("click", () => onSelect(p));
      }
      mk.addTo(map);
      ref.current.push(mk);
    });

    if (showAdvertisers) {
      (advertisers || []).forEach((a) => {
        if (a.latitude == null || a.longitude == null) return;
        const icon = L.divIcon({
          className: "advertiser-pin",
          html: `<div style="width:24px;height:24px;border-radius:50%;background:#059669;border:2px solid #fff;box-shadow:0 1px 5px rgba(0,0,0,.5);display:flex;align-items:center;justify-content:center"><span style="color:#fff;font-size:13px;font-weight:800">$</span></div>`,
          iconSize: [24, 24], iconAnchor: [12, 12],
        });
        const mk = L.marker([a.latitude, a.longitude], { icon, zIndexOffset: 1000 });
        mk.bindTooltip(`${a.name || "Advertiser"}${a.status ? ` · ${a.status}` : ""}`, { direction: "top" });
        const esc = (s) => String(s == null ? "" : s).replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]));
        const rows = [`<div style="font-weight:700;font-size:13px">${esc(a.name || "Business")}</div>`];
        if (a.category) rows.push(`<div style="font-size:11px;color:#6b7280;text-transform:capitalize">${esc(a.category)}</div>`);
        if (a.city) rows.push(`<div style="font-size:12px;margin-top:4px;color:#374151">${esc(a.city)}</div>`);
        if (a.status) rows.push(`<div style="font-size:11px;margin-top:4px;color:#059669;text-transform:capitalize;font-weight:600">${esc(a.status)}</div>`);
        mk.bindPopup(`<div style="min-width:160px">${rows.join("")}</div>`);
        mk.addTo(map);
        ref.current.push(mk);
      });
    }

    return () => { ref.current.forEach((m) => map.removeLayer(m)); ref.current = []; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, prospects, advertisers, showAdvertisers, readOnly]);

  return null;
}
